<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';

	import { sub_units } from 'src/stores/account/home';

	import {
		channelForNotificationType,
		getCurrentUserDisplayName,
		getEligibleSubUnitRecipients,
		getHomeOwnerEmail,
		labelForSubUnitId,
		reconcileSubUnitRecipients,
	} from 'src/components/Scenes/helpers/notification_recipients_helpers';

	import { Alert, CardGrid, Text, SelectCard } from '@Shelly/shelly-design-system';
	import type { NotificationActionUI } from 'src/types/NotificationActionUI';
	import type { NotificationChannel, SubUnitRecipientOption } from 'src/types/NotificationRecipients';

	export let sceneData: any;

	$: notificationType = sceneData?.notificationAction?.type as
		NotificationActionUI['type'] | undefined;
	$: channel = channelForNotificationType(notificationType);

	/**
	 * Re-derive eligible sub-unit recipients whenever the `sub_units` store
	 * changes (e.g. when the active home is switched while the wizard is open)
	 * or the notification channel switches between push and mail (`nperm`
	 * flags differ).
	 */
	$: subUnitRecipients = (() => {
		void $sub_units;
		void channel;
		return channel ? getEligibleSubUnitRecipients(channel) : ([] as SubUnitRecipientOption[]);
	})();

	$: ownerEmail = getHomeOwnerEmail();
	$: ownerDisplayName = getCurrentUserDisplayName();

	/**
	 * `pushSkipUnit` lives on each notification action. Toggling the "include
	 * me" row mutates the current action only — other notification actions on
	 * the same scene keep their own owner-inclusion state.
	 */
	$: ownerIncluded = !sceneData?.notificationAction?.pushSkipUnit;

	const setOwnerIncluded = (next: boolean): void => {
		if (!sceneData?.notificationAction) return;
		const action = { ...sceneData.notificationAction };
		if (next) {
			delete action.pushSkipUnit;
		} else {
			action.pushSkipUnit = true;
		}
		sceneData.notificationAction = action;
	};

	const readCurrentSubIds = (): number[] => {
		const raw = sceneData?.notificationAction?.toSub;
		if (!Array.isArray(raw)) return [];
		const seen = new Set<number>();
		const cleaned: number[] = [];
		for (const value of raw) {
			const id = Number(value);
			if (!Number.isFinite(id) || seen.has(id)) continue;
			seen.add(id);
			cleaned.push(id);
		}
		return cleaned;
	};

	const isSelected = (subId: number, list: number[]): boolean => list.includes(subId);

	const toggleSubUnit = (subId: number, hasChannelPermission: boolean): void => {
		if (!hasChannelPermission) return;
		const current = readCurrentSubIds();
		const idx = current.indexOf(subId);
		if (idx >= 0) {
			current.splice(idx, 1);
		} else {
			current.push(subId);
		}
		sceneData.notificationAction = {
			...sceneData.notificationAction,
			toSub: current,
		};
	};

	const removeOrphan = (subId: number): void => {
		const current = readCurrentSubIds().filter((id) => id !== subId);
		sceneData.notificationAction = {
			...sceneData.notificationAction,
			toSub: current,
		};
	};

	const recipientCardDescriptiveText = (disabled: boolean, email: string): string => {
		const translate = get(format);
		if (disabled) {
			return translate('scene_notification_recipient_disabled_channel', {
				default: 'This member has disabled this notification channel in their account settings.',
			});
		}
		return email;
	};

	const reconcileForUi = (
		toSub: number[] | undefined,
		ch: NotificationChannel | undefined,
	): { selected: number[]; orphans: number[] } => {
		if (!ch) return { selected: [], orphans: [] };
		return reconcileSubUnitRecipients(toSub);
	};

	$: ({ selected, orphans } = (() => {
		void $sub_units;
		return reconcileForUi(sceneData?.notificationAction?.toSub, channel);
	})());

	$: hasAnyRecipient = ownerIncluded || selected.length > 0 || orphans.length > 0;
</script>

{#if channel}
	<Text element="h2" class="mb-s mt-l">
		{$format('scene_notification_recipients_title', {
			default: 'Send to',
		})}
	</Text>
	<Text class="mb-s text-color-muted" styleType="body-small">
		{$format('scene_notification_recipients_info', {
			default:
				'Choose which members of this shared home will receive the notification when the scene is triggered.',
		})}
	</Text>

	<CardGrid gridGap="s" sm={270} lg={290} noPaddingBottom>
		<div class="d-flex">
			<SelectCard
				class="width-full"
				type="horizontal"
				variant="checkbox"
				icon="fal fa-house"
				title={$format('scene_notification_recipient_owner_me_label', {
					default: '{name} (me)',
					values: { name: ownerDisplayName || ownerEmail },
				})}
				descriptiveText={ownerEmail ||
					$format('scene_notification_recipient_owner_hint', {
						default: 'Home owner',
					})}
				selected={ownerIncluded}
				on:click={() => setOwnerIncluded(!ownerIncluded)}
			/>
		</div>

		{#each subUnitRecipients as recipient (recipient.subId)}
			{@const checked = isSelected(recipient.subId, selected)}
			{@const disabled = !recipient.hasChannelPermission}
			<div class="d-flex">
				<SelectCard
					class="width-full"
					type="horizontal"
					variant="checkbox"
					icon="fal fa-user"
					title={recipient.isCurrentUser
						? $format('scene_notification_recipient_current_user_label', {
								default: '{name} (you)',
								values: { name: recipient.name },
							})
						: recipient.name}
					descriptiveText={recipientCardDescriptiveText(disabled, recipient.email)}
					selected={checked}
					{disabled}
					on:click={() => toggleSubUnit(recipient.subId, recipient.hasChannelPermission)}
				/>
			</div>
		{/each}
	</CardGrid>

	{#if orphans.length > 0}
		<Alert
			class="mt-s"
			type="warning"
			heading={$format('scene_notification_recipient_orphans_title', {
				default: 'Some recipients are no longer in this home',
			})}
			message={$format('scene_notification_recipient_orphans_info', {
				default:
					'The following members used to receive this notification but no longer have access to this home. Remove them to keep the scene tidy.',
			})}
		/>
		<CardGrid gridGap="s" sm={270} lg={290} noPaddingBottom class="mt-s">
			{#each orphans as orphan (orphan)}
				<div class="d-flex">
					<SelectCard
						class="width-full"
						type="horizontal"
						variant="checkbox"
						icon="fas fa-user-slash"
						title={labelForSubUnitId(orphan)}
						descriptiveText={$format('scene_notification_recipient_no_longer_receiving', {
							default: 'No longer receiving — click to remove',
						})}
						selected
						on:click={() => removeOrphan(orphan)}
					/>
				</div>
			{/each}
		</CardGrid>
	{/if}

	{#if !hasAnyRecipient}
		<Alert
			class="mt-s"
			type="warning"
			heading={$format('scene_notification_recipients_empty_warn', {
				default: 'Pick at least one recipient',
			})}
			message={$format('scene_notification_recipients_empty_info', {
				default: 'Select at least one home member to receive this notification.',
			})}
		/>
	{/if}
{/if}
